import type { RealtimeVoiceCallCopy } from '@/i18n/realtime-voice-call-copy';
import type { RealtimeVoiceFailureKind } from '@/components/org-chat/realtime-voice-lifecycle';
import { REALTIME_VOICE_MIN_START_CREDITS } from '@/convex/config/pricing_constants';
import { formatInsufficientCreditsMessage } from '@/lib/credit-costs';

type RealtimeSessionErrorLike = {
  type?: unknown;
  error?: unknown;
};

function getErrorEventName(error: unknown): string {
  if (
    typeof Event !== 'undefined' &&
    error instanceof Event &&
    typeof error.type === 'string'
  ) {
    return error.type;
  }
  if (error && typeof error === 'object' && 'type' in error) {
    const type = (error as { type?: unknown }).type;
    if (typeof type === 'string') return type;
  }
  return '';
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object') {
    const candidate = error as { message?: unknown; error?: unknown };
    if (typeof candidate.message === 'string') return candidate.message;
    if (candidate.error && candidate.error !== error) {
      return getErrorMessage(candidate.error);
    }
  }
  return typeof error === 'string' ? error : '';
}

export function getRealtimeSessionErrorSummary(event: unknown): {
  message: string;
  name?: string;
  code?: string;
  eventType?: string;
} {
  const envelope = event as RealtimeSessionErrorLike | null;
  const underlying = envelope?.error || event;
  const message = getErrorMessage(underlying);
  const eventType = getErrorEventName(underlying) || getErrorEventName(event);
  const details =
    underlying && typeof underlying === 'object'
      ? (underlying as { name?: unknown; code?: unknown })
      : null;

  return {
    message: message || eventType || 'Unknown realtime session error',
    ...(typeof details?.name === 'string' ? { name: details.name } : {}),
    ...(typeof details?.code === 'string' ? { code: details.code } : {}),
    ...(eventType ? { eventType } : {}),
  };
}

export function getRealtimeVoiceFailureMessage(
  copy: RealtimeVoiceCallCopy,
  failure: RealtimeVoiceFailureKind
): string {
  if (failure === 'permission') return copy.permissionError;
  if (failure === 'capability') return copy.capabilityError;
  if (failure === 'capacity') return copy.capacityError;
  if (failure === 'timeout') return copy.timeoutError;
  if (failure === 'connection') return copy.connectionError;
  if (failure === 'insufficient_credits')
    return formatInsufficientCreditsMessage(
      copy.insufficientCreditsError,
      REALTIME_VOICE_MIN_START_CREDITS
    );
  return copy.startupError;
}

export function shouldIgnoreRealtimeSessionError(args: {
  event: unknown;
  aborted: boolean;
  mounted: boolean;
}): boolean {
  void args.event;
  return args.aborted || !args.mounted;
}
